import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiMutate } from "@/lib/api";
import type { StarterCardDef } from "../../shared/gadget-starters";
import type { ArtifactHealth } from "../../shared/artifact-health";

/** #2075: starter cards the viewer's role can run (server-gated on every binding tool). */
export function useStarters() {
  return useQuery({
    queryKey: ["gadget-starters"],
    queryFn: () => apiFetch<{ starters: StarterCardDef[] }>("/api/artifacts/starters"),
    staleTime: 10 * 60 * 1000,
  });
}

export interface ArtifactSummary {
  id: string;
  title: string;
  /** Row type is CHECK-constrained in D1 to these three. AI-generated
   *  artifacts are typed "chart" — detect them via spec_json.artifact_status. */
  type: "chart" | "table" | "summary";
  tags: string | null;
  description: string | null;
  is_shared: number;
  created_at: string;
  updated_at: string;
  /** #1979: non-null = gadget (live bindings). The list route returns it; detail may too. */
  manifest_json?: string | null;
  /** #2087: last nightly health verdict; null until the sweep has seen the row. */
  health?: ArtifactHealth | null;
}

export interface ArtifactDetail extends Omit<ArtifactSummary, "tags"> {
  spec_json: Record<string, unknown>;
  sql_query: string | null;
  tags: string[];
  /** #2074: false when this row reached the viewer through a share to their role (read only). */
  owned?: boolean;
  /** #2074: who shared it, for a recipient. */
  shared_by_name?: string | null;
}

/** #2074: a gadget shared to one of the viewer's roles by someone else. */
export interface SharedArtifactSummary extends Pick<ArtifactSummary, "id" | "title" | "type" | "description" | "created_at" | "updated_at" | "manifest_json"> {
  role_id: string;
  shared_by: string;
  shared_by_name: string | null;
}

export function useSharedArtifacts(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["artifacts-shared"],
    queryFn: () => apiFetch<{ artifacts: SharedArtifactSummary[]; pending_migration?: boolean }>("/api/artifacts/shared"),
    enabled: opts.enabled ?? true,
  });
}

/** #2074: roles a gadget can be shared to (id + name only). */
export function useShareRoles(enabled: boolean) {
  return useQuery({
    queryKey: ["artifact-share-roles"],
    queryFn: () => apiFetch<{ roles: { id: string; name: string }[] }>("/api/artifacts/share-roles"),
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

/** #2074: the roles one of MY gadgets is shared to. */
export function useArtifactShares(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ["artifact-shares", id],
    queryFn: () => apiFetch<{ roles: string[]; pending_migration?: boolean }>(`/api/artifacts/${id}/shares`),
    enabled,
  });
}

export function useSetArtifactShares() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, roles }: { id: string; roles: string[] }) =>
      apiMutate<{ ok: boolean; roles: string[] }>(`/api/artifacts/${id}/shares`, "PUT", { roles }),
    onMutate: async ({ id, roles }) => {
      await qc.cancelQueries({ queryKey: ["artifact-shares", id] });
      const prev = qc.getQueryData<{ roles: string[] }>(["artifact-shares", id]);
      qc.setQueryData(["artifact-shares", id], { roles });
      return { prev };
    },
    onError: (_err, { id }, ctx) => { if (ctx?.prev) qc.setQueryData(["artifact-shares", id], ctx.prev); },
    onSettled: (_data, _err, { id }) => qc.invalidateQueries({ queryKey: ["artifact-shares", id] }),
  });
}

/** #2075: starter cards create a gadget through the same generate path chat uses. */
export function useGenerateArtifact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { title: string; description: string; manifest?: unknown; type?: "html" | "svg" | "mermaid"; pin?: boolean }) =>
      apiMutate<{ id: string; status: string; pinned?: boolean }>("/api/artifacts/generate", "POST", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["artifacts"] });
      // #2075: a starter create pins itself (pin: true); Overview reads the pins list.
      qc.invalidateQueries({ queryKey: ["gadget-pins"] });
    },
  });
}

export function useArtifacts(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["artifacts"],
    queryFn: () => apiFetch<ArtifactSummary[]>("/api/artifacts"),
    enabled: opts.enabled ?? true,
  });
}

export function useArtifact(id: string | null) {
  return useQuery({
    queryKey: ["artifact", id],
    queryFn: () => apiFetch<ArtifactDetail>(`/api/artifacts/${id}`),
    enabled: id !== null,
  });
}

export function useSaveArtifact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      title: string;
      type: "chart" | "table" | "summary";
      spec_json: unknown;
      sql_query?: string;
      description?: string;
      tags?: string[];
    }) => apiMutate<{ id: string; title: string }>("/api/artifacts", "POST", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["artifacts"] }),
  });
}

/**
 * #4087 ARTIFACTEDIT: the owner renames or retags one of their own artifacts.
 * The body is exactly { title, tags }: the server reads an omitted key as
 * unchanged, so sharing, the manifest and the description are never touched
 * from here. PUT /api/artifacts/:id updates only the caller's own row (404
 * otherwise). The card and the open page read the list and detail caches, so
 * both are patched at once and then refetched to pick up updated_at.
 */
export function useUpdateArtifactDetails() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title, tags }: { id: string; title: string; tags: string[] }) =>
      apiMutate<{ ok: boolean }>(`/api/artifacts/${id}`, "PUT", { title, tags }),
    onSuccess: (_data, { id, title, tags }) => {
      const stored = tags.length ? JSON.stringify(tags) : null;
      qc.setQueriesData<ArtifactSummary[] | undefined>({ queryKey: ["artifacts"] }, (rows) =>
        Array.isArray(rows) ? rows.map((r) => (r.id === id ? { ...r, title, tags: stored } : r)) : rows,
      );
      qc.setQueriesData<ArtifactDetail | undefined>({ queryKey: ["artifact", id] }, (cur) =>
        cur && typeof cur === "object" && !Array.isArray(cur) ? { ...cur, title, tags } : cur,
      );
      qc.invalidateQueries({ queryKey: ["artifacts"] });
      qc.invalidateQueries({ queryKey: ["artifact", id] });
    },
  });
}

/** #3372: the pins list as the Overview row reads it (lib/gadget-pins.ts owns the key). */
type PinsCache = { pins: { artifact_id: string; position: number }[]; pending_migration?: boolean };

/** #3372: drop one gadget from this user's pins in the cache, so Overview loses it at once. */
function dropPin(qc: ReturnType<typeof useQueryClient>, id: string) {
  qc.setQueriesData<PinsCache | undefined>({ queryKey: ["gadget-pins"] }, (cur) =>
    cur && Array.isArray(cur.pins) ? { ...cur, pins: cur.pins.filter((p) => p.artifact_id !== id) } : cur,
  );
  qc.invalidateQueries({ queryKey: ["gadget-pins"] });
}

/** Owner delete. The server removes the row, its share rows, every pin and every
 *  viewer's storage in one request; the client drops it from the list and from
 *  Overview straight away (#3372), then refetches to confirm. */
export function useDeleteArtifact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiMutate<{ ok: boolean }>(`/api/artifacts/${id}`, "DELETE"),
    onSuccess: (_data, id) => {
      qc.setQueriesData<ArtifactSummary[] | undefined>({ queryKey: ["artifacts"] }, (rows) =>
        Array.isArray(rows) ? rows.filter((r) => r.id !== id) : rows,
      );
      qc.invalidateQueries({ queryKey: ["artifacts"] });
      dropPin(qc, id);
    },
  });
}

/** #3372: a recipient removes a gadget shared to their role from their own list.
 *  The owner and everyone else the gadget is shared with keep it. */
export function useRemoveSharedArtifact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiMutate<{ ok: boolean; removed: boolean }>(`/api/artifacts/${id}/shares/me`, "DELETE"),
    onSuccess: (_data, id) => {
      qc.setQueriesData<{ artifacts: SharedArtifactSummary[]; pending_migration?: boolean } | undefined>({ queryKey: ["artifacts-shared"] }, (cur) =>
        cur && Array.isArray(cur.artifacts) ? { ...cur, artifacts: cur.artifacts.filter((a) => a.id !== id) } : cur,
      );
      qc.invalidateQueries({ queryKey: ["artifacts-shared"] });
      dropPin(qc, id);
    },
  });
}

export interface ArtifactStatus {
  status: "generating" | "ready" | "expired" | "error" | "unknown";
  has_code?: boolean;
  error?: string | null;
  /** #2075: "error" because the generating row went silent for 3 minutes, not a model failure. */
  stale?: boolean;
}

/**
 * Generation status for AI-generated artifacts (GH #1086). Polls every 4s
 * while the artifact is still generating, then stops.
 */
export function useArtifactStatus(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ["artifact-status", id],
    queryFn: () => apiFetch<ArtifactStatus>(`/api/artifacts/${id}/status`),
    enabled,
    refetchInterval: (query) =>
      query.state.data?.status === "generating" ? 4000 : false,
  });
}

export function useRegenerateArtifact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiMutate<{ id: string; status: string }>(`/api/artifacts/${id}/regenerate`, "POST"),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["artifact-status", id] });
      qc.invalidateQueries({ queryKey: ["artifact", id] });
      // #2087: the card's health badge reads the list cache; the server clears
      // spec_json.health on regenerate. Patch that one row in place rather than
      // refetching the whole library (mutation-invalidation test pins the keys).
      qc.setQueriesData<ArtifactSummary[] | undefined>({ queryKey: ["artifacts"] }, (rows) =>
        rows?.map((r) => (r.id === id ? { ...r, health: null } : r)),
      );
    },
  });
}

export function useRefreshArtifact() {
  const qc = useQueryClient();
  return useMutation({
    // #1498: keep=true snapshots the current data as a dated entry before the
    // refresh replaces it ("Refresh & keep"); plain refresh is unchanged.
    mutationFn: ({ id, keep }: { id: string; keep?: boolean }) =>
      apiMutate<{ ok: boolean; row_count: number; spec: Record<string, unknown>; kept?: boolean; note?: string }>(
        `/api/artifacts/${id}/refresh`,
        "POST",
        keep ? { keep: true } : undefined
      ),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["artifacts"] });
      qc.invalidateQueries({ queryKey: ["artifact", id] });
      qc.invalidateQueries({ queryKey: ["artifact-snapshots", id] });
    },
  });
}

export interface ArtifactSnapshot {
  id: number;
  label: string;
  row_count: number;
  captured_at: string;
  data: unknown[];
  sql_query: string | null;
}

/** #1498: dated snapshot list for an artifact (newest first). */
export function useArtifactSnapshots(id: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["artifact-snapshots", id],
    queryFn: () =>
      apiFetch<{ snapshots: ArtifactSnapshot[]; pending_migration?: boolean }>(
        `/api/artifacts/${id}/snapshots`
      ),
    enabled: !!id && enabled,
    staleTime: 60 * 1000,
  });
}
